import React, { useState } from 'react';
import Form from '../../Form/Form';
import { CommercialCalculatorRefinanceForm } from '../../../components/Forms';
import {
  RefinanceLoanTable,
  LenderPremiumTable
} from '../../../components/Tables';
import {
  calculateCredit,
  premiumLookup,
  calculatePremium
} from '../../../../lib/helpers';

const CommercialCalculatorRefinance = ({
  calculatorVariableConstants,
  calculatorConstants,
  date
}) => {
  // destructuring the calculator constants set within the WP admin.
  const {
    assessmentRecoupmentCharge,
    taxDeletion,
    notDuePayable,
    escrowFee,
    t_23Access,
    t_25Contiguity,
    t_28,
    t_33,
    t_36,
    t_39,
    estimatedRecordingFee
  } = calculatorConstants;

  // defining initial input values used as the formProps
  const initialCalculationInputValues = {
    borrower: '',
    newLoanAmount: '',
    balanceExistingLoan: '',
    originalDateLoan: date
  };

  // defining initial calulation results for the refinance loan component
  const initialRefinanceLoanResults = {
    newLoanPremium: 0.0,
    existingLoanPremium: 0.0,
    percentCredit: calculatePercentCredit(date),
    resultingCredit: 0.0
  };

  // defining initial calulation results for the refinance lender component
  const initialLenderCalculationResults = {
    assessmentRecoupmentCharge: Number(assessmentRecoupmentCharge),
    taxDeletion: Number(taxDeletion),
    notDuePayable: Number(notDuePayable),
    t19Result: 0.0,
    t23LenderResult: 0.0,
    t25LenderResult: 0.0,
    t28LenderResult: 0.0,
    t33LenderResult: 0.0,
    t36LenderResult: 0.0,
    t39LenderResult: 0.0,
    escrowFee: Number(escrowFee),
    estimatedRecordingFee: Number(estimatedRecordingFee)
  };

  // defining the inital checkbox checked state for the lender table
  const checkboxConstants = {
    lender: {
      t_19_lender: false,
      t_23_lender: false,
      t_25_lender: false,
      t_28_lender: false,
      t_33_lender: false,
      t_36_lender: false,
      t_39_lender: false
    }
  };

  // Setting the inital state of the results using the useState hook
  const [refinanceLoanResults, setRefinanceLoanResults] = useState(
    initialRefinanceLoanResults
  );

  const [lenderTitleResults, setLenderTitleResults] = useState(
    initialLenderCalculationResults
  );

  // calculate the loan premium value based on the loan amount set
  function calculateLoanPremium(loanAmount) {
    return calculatePremium(loanAmount);
  }

  // calculate the t_19 value based on the loan amount set
  function t19(loanAmount) {
    const loan = calculateLoanPremium(loanAmount);
    return loan * 0.05 < 50 ? 50 : loan * 0.1;
  }

  // calculate the loan age from the date of the existing loan
  function calculateLoanAge(existingLoanDate) {
    if (existingLoanDate !== '' || existingLoanDate !== undefined) {
      const existingDate = new Date(existingLoanDate);

      if (existingDate.getUTCFullYear() < 2000) {
        existingDate.setFullYear(existingDate.getUTCFullYear() + 100);
      }

      return new Date() - existingDate;
    }

    return 0;
  }

  // calculate percentage credit based on the loan age
  function calculatePercentCredit(loanDate) {
    const loadAge = calculateLoanAge(loanDate);
    const loanAgeInDays = loadAge / 86400000;
    const loanAgeInYears = loanAgeInDays / 365;
    return calculateCredit(loanAgeInYears);
  }

  // calculate the resulting credit from the existing loan and the percentage credit.
  function calculateResultingCredit(existingLoanPremium, percentCredit) {
    return existingLoanPremium * percentCredit;
  }

  // calculating totals
  function totalAdjustedPremium() {
    const { newLoanPremium, resultingCredit } = refinanceLoanResults;
    const premium = newLoanPremium - resultingCredit;

    if (premium <= 0) {
      return 0.0;
    } else if (premium < premiumLookup[10000]) {
      return premiumLookup[10000];
    }

    return premium;
  }

  // calculating totals
  function totalLenderPolicyPremium() {
    const {
      assessmentRecoupmentCharge,
      taxDeletion,
      notDuePayable,
      t19Result,
      t23LenderResult,
      t25LenderResult,
      t28LenderResult,
      t33LenderResult,
      t36LenderResult,
      t39LenderResult
    } = lenderTitleResults;
    const adjustedPremium = totalAdjustedPremium();

    return (
      adjustedPremium +
      assessmentRecoupmentCharge +
      taxDeletion +
      notDuePayable +
      t19Result +
      t23LenderResult +
      t25LenderResult +
      t28LenderResult +
      t33LenderResult +
      t36LenderResult +
      t39LenderResult
    );
  }

  // calculating totals
  function totalLenderPolicyAndEscrowFee() {
    return totalLenderPolicyPremium() + lenderTitleResults.escrowFee;
  }

  // Run all the calculations and set the sate of the results accordingly
  function runCalculations({
    newLoanAmount,
    balanceExistingLoan,
    originalDateLoan,
    ...values
  }) {
    const newLoanPremium = calculateLoanPremium(
      newLoanAmount.replace(/,/g, '')
    );
    const existingLoanPremium = calculateLoanPremium(
      balanceExistingLoan.replace(/,/g, '')
    );
    const percentCredit = calculatePercentCredit(originalDateLoan);
    const resultingCredit = calculateResultingCredit(
      existingLoanPremium,
      percentCredit
    );
    const t19Result = t19(newLoanAmount.replace(/,/g, ''));

    if (t_19_lender.value === 'true') {
      setLenderTitleResults({
        ...lenderTitleResults,
        t19Result: t19Result
      });
    }

    setRefinanceLoanResults({
      ...refinanceLoanResults,
      newLoanPremium,
      existingLoanPremium,
      percentCredit,
      resultingCredit
    });
  }

  // Setting the state of the table checkboxes passed up from the tables components
  function setCheckedLenders(status) {
    const {
      t_19_lender,
      t_23_lender,
      t_25_lender,
      t_28_lender,
      t_33_lender,
      t_36_lender,
      t_39_lender
    } = status;
    const {
      t19Result,
      t23LenderResult,
      t25LenderResult,
      t28LenderResult,
      t33LenderResult,
      t36LenderResult,
      t39LenderResult
    } = initialLenderCalculationResults;
    const t19Lender = t19(newLoanAmount.value.replace(/,/g, ''));

    // setting lenderTitleResults state of all results
    setLenderTitleResults({
      ...lenderTitleResults,
      t19Result: t_19_lender === true ? t19Lender : t19Result,
      t23LenderResult:
        t_23_lender === true ? Number(t_23Access) : t23LenderResult,
      t25LenderResult:
        t_25_lender === true ? Number(t_25Contiguity) : t25LenderResult,
      t28LenderResult: t_28_lender === true ? Number(t_28) : t28LenderResult,
      t33LenderResult: t_33_lender === true ? Number(t_33) : t33LenderResult,
      t36LenderResult: t_36_lender === true ? Number(t_36) : t36LenderResult,
      t39LenderResult: t_39_lender === true ? Number(t_39) : t39LenderResult
    });
  }

  // Overriding Form component handleInputBlur functions
  function handleInputBlur(event, formProps) {
    formProps.handleInputChange(event);
    runCalculations(formProps.values);
  }

  // Overriding Form component handleInputChange functions
  function handleInputChange(event, formProps) {
    formProps.handleInputChange(event);
    formProps.values[event.target.name] = event.target.value;
    runCalculations(formProps.values);
  }

  return (
    <>
      <div className="grid grid--gutter grid--middle u--margin-bottom-30">
        <div className="grid__col-xs-12 grid__col-md-6">
          <Form
            className="calc__box calc__box--tabled"
            initialCalculationInputValues={initialCalculationInputValues}
            render={({ formProps }) => (
              <CommercialCalculatorRefinanceForm
                formProps={{
                  ...formProps,
                  handleInputChange: event =>
                    handleInputChange(event, formProps)
                }}
              />
            )}
          />
        </div>
        <div className="grid__col-xs-12 grid__col-md-6">
          <RefinanceLoanTable
            title="Premium"
            subtitle=""
            totalAdjustedPremium={totalAdjustedPremium()}
            className="table table--striped"
            refinanceLoanResults={refinanceLoanResults}
          />
        </div>
      </div>
      <div className="grid grid--center">
        <div className="grid__col-xs-12 grid__col-md-10">
          <LenderPremiumTable
            title="Lender's Title Policy Premium"
            subtitle="Please select applicable fees"
            calculatorType="commercialRefinance"
            checkboxConstants={checkboxConstants.lender}
            className="table table--striped"
            lenderTitleResults={lenderTitleResults}
            setCheckedLenders={setCheckedLenders}
            totals={{
              totalAdjustedPremium: totalAdjustedPremium(),
              totalLenderPolicyAndEscrowFee: totalLenderPolicyAndEscrowFee(),
              totalLenderPolicyPremium: totalLenderPolicyPremium()
            }}
          />
        </div>
      </div>
    </>
  );
};

export default CommercialCalculatorRefinance;
